import { useMemo } from 'react';
import { Typography, Space, Layout } from 'antd';
import { useAuthStore } from '@/stores/authStore';
import { helpTopics } from '@/constants/helpContent';
import { HelpSearch } from '@/components/help/HelpSearch';
import { HelpCategoryList } from '@/components/help/HelpCategoryList';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';

const { Title, Paragraph } = Typography;
const { Content } = Layout;

/**
 * 公開ヘルプページ（/help）
 *
 * 機能:
 * - ログイン不要でアクセス可能
 * - ユーザーの権限に応じてトピックをフィルタリング
 * - カテゴリ一覧表示
 * - 検索機能
 */
export function HelpPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();

  // ユーザーの権限に応じてトピックをフィルタリング
  const visibleTopics = useMemo(() => {
    return helpTopics.filter((topic) => {
      // 公開トピック
      if (!topic.requiresAuth) return true;

      // ログイン必須
      if (!isAuthenticated) return false;

      // ロール制限
      if (topic.roles && !topic.roles.includes(user?.role || '')) {
        return false;
      }

      return true;
    });
  }, [isAuthenticated, user?.role]);

  const handleSelectTopic = (topicId: string) => {
    // 詳細ページに遷移
    navigate(`/help/${topicId}`);
  };

  return (
    <>
      <SEOHead
        title="ヘルプ｜一元帳"
        description="一元帳のヘルプページです。操作方法や機能の使い方をガイドします。"
      />
      <Layout style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <Content style={{ padding: '24px 48px', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
        {/* ヘッダー */}
        <Space direction="vertical" size="large" style={{ width: '100%', marginBottom: '40px' }}>
          <div>
            <Title level={1} style={{ marginBottom: '8px' }}>
              ヘルプセンター
            </Title>
            <Paragraph style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>
              一元帳の使い方を確認できます。質問がある場合は、下記の検索またはカテゴリからお探しください。
            </Paragraph>
          </div>

          {/* 検索 */}
          <div style={{ maxWidth: '600px' }}>
            <HelpSearch onSelectTopic={handleSelectTopic} />
          </div>
        </Space>

        {/* カテゴリ一覧 */}
        <HelpCategoryList topics={visibleTopics} onSelectTopic={handleSelectTopic} />

        {/* ログインを促すメッセージ（未ログインユーザー向け） */}
        {!isAuthenticated && (
          <div style={{
            marginTop: '48px',
            padding: '24px',
            backgroundColor: '#f0f5ff',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <Title level={4}>より詳しいヘルプをご覧になりますか？</Title>
            <Paragraph style={{ marginBottom: '16px' }}>
              ログインすると、すべての機能のヘルプをご覧いただけます。
            </Paragraph>
            <a href="/login" style={{ fontSize: '16px', fontWeight: 500 }}>
              ログインする →
            </a>
          </div>
        )}
      </Content>
    </Layout>
    </>
  );
}
